/**
 * URL Generation Utilities
 * Ported from: innovation-claude/packages/tradovate-client/src/config/urls.rs
 *
 * Generates API URLs based on stage × environment matrix
 * Uses explicit hostname mapping for each combination (only Devel stage fully implemented currently)
 */

import type { TradovateStage, TradovateEnvironment } from './types'

/**
 * URL builder options - baseApiUrl is REQUIRED to prevent accidental production connections
 */
export interface UrlBuilderOptions {
  /**
   * Base host suffix to append to environment prefix.
   * Examples:
   *  - '-d.tradovateapi.com' (devel)
   *  - '-api.staging.ninjatrader.dev' (staging)
   *  - '-api.uat.ninjatrader.dev' (uat)
   *  - '.tradovateapi.com' (production)
   *
   * If a full URL is provided (e.g. 'https://tradovateapi.com'), the host is
   * extracted and converted to '.tradovateapi.com'.
   *
   * REQUIRED: This field must be explicitly provided to prevent accidental
   * connections to production environment.
   */
  baseApiUrl: string
}

function getBaseSuffix(baseApiUrl: string): string {
  // If it looks like a URL, reduce to host suffix: '.host'
  try {
    const u = new URL(baseApiUrl)
    return `.${u.host}`
  } catch {
    // Assume already a suffix (e.g., '-d.tradovateapi.com' or '.tradovateapi.com')
    return baseApiUrl
  }
}

/**
 * Get primary hostname for stage + environment
 * Ported from urls.rs:47-69
 */
function getHostname(_stage: TradovateStage, env: TradovateEnvironment, baseSuffix: string): string {
  const e = env.toLowerCase()
  return `${e}${baseSuffix}`
}

/**
 * Get market data hostname (separate from primary)
 * Ported from urls.rs:90-111
 *
 * Returns null for Replay/TryIt (they use primary hostname)
 */
function getMdHostname(_stage: TradovateStage, env: TradovateEnvironment, baseSuffix: string): string | null {
  const e = env.toLowerCase()
  // If Replay/TryIt should share primary, return null to fallback
  if (env === 'Replay' || env === 'TryIt') return null
  return `md-${e}${baseSuffix}`
}

/**
 * Get reporting hostname
 * Ported from urls.rs:123-147
 */
function getReportingHostname(_stage: TradovateStage, env: TradovateEnvironment, baseSuffix: string): string {
  const e = env.toLowerCase()
  return `rpt-${e}${baseSuffix}`
}

/**
 * Get remote storage hostname
 * Ported from urls.rs:167-185
 *
 * NOTE: Storage URLs are stage-specific but NOT environment-specific
 */
// Note: legacy stage-specific mapping retained for reference.
// Storage hostname is now derived from a single base suffix; see buildRemoteStorageHttpUrl.

/**
 * Build primary HTTP URL
 * Ported from urls.rs:72-78
 */
export function buildPrimaryHttpUrl(stage: TradovateStage, env: TradovateEnvironment, opts: UrlBuilderOptions): string {
    const baseSuffix = getBaseSuffix(opts.baseApiUrl)
    const hostname = getHostname(stage, env, baseSuffix)
    return `https://${hostname}/v1`
}

/**
 * Build authentication HTTP URL
 * Ported from urls.rs:159-164
 * NOTE: Auth uses primary HTTP URL
 */
export function buildAuthHttpUrl(stage: TradovateStage, env: TradovateEnvironment, opts: UrlBuilderOptions): string {
    return buildPrimaryHttpUrl(stage, env, opts)
}

/**
 * Build primary WebSocket URL
 * Ported from urls.rs:81-87
 */
export function buildPrimaryWsUrl(stage: TradovateStage, env: TradovateEnvironment, opts: UrlBuilderOptions): string {
    const baseSuffix = getBaseSuffix(opts.baseApiUrl)
    const hostname = getHostname(stage, env, baseSuffix)
    return `wss://${hostname}/v1/websocket`
}

/**
 * Build market data WebSocket URL
 * Ported from urls.rs:114-120
 *
 * Uses dedicated MD hostname for Demo/Live, falls back to primary for Replay/TryIt
 */
export function buildMdWsUrl(stage: TradovateStage, env: TradovateEnvironment, opts: UrlBuilderOptions): string {
    const baseSuffix = getBaseSuffix(opts.baseApiUrl)
    const mdHostname = getMdHostname(stage, env, baseSuffix)
    const hostname = mdHostname || getHostname(stage, env, baseSuffix)  // Fallback to primary
    return `wss://${hostname}/v1/websocket`
}

/**
 * Build reporting HTTP URL
 * Ported from urls.rs:150-156
 */
export function buildReportingHttpUrl(stage: TradovateStage, env: TradovateEnvironment, opts: UrlBuilderOptions): string {
    const baseSuffix = getBaseSuffix(opts.baseApiUrl)
    const hostname = getReportingHostname(stage, env, baseSuffix)
    return `https://${hostname}/v1`
}

/**
 * Build remote storage HTTP URL
 * Ported from urls.rs:167-185
 *
 * NOTE: Storage is stage-specific, not environment-specific
 * Returns base URL without /storage suffix - RemoteBackend adds /user/{userId}/items/{key}
 */
export function buildRemoteStorageHttpUrl(_stage: TradovateStage, _env: TradovateEnvironment, opts: UrlBuilderOptions): string {
    // Storage does not depend on environment; derive from a single base suffix
    // Examples:
    //   baseSuffix='.tradovateapi.com' => 'https://config.tradovateapi.com/'
    //   baseSuffix='-d.tradovateapi.com' => 'https://config-d.tradovateapi.com/'
    //   baseSuffix='-api.staging.ninjatrader.dev' => 'https://config-api.staging.ninjatrader.dev/'
    //   baseSuffix='-api.uat.ninjatrader.dev' => 'https://config-api.uat.ninjatrader.dev/'
    const baseSuffix = getBaseSuffix(opts.baseApiUrl)
    const storageHost = `config${baseSuffix}`
    return `https://${storageHost}/`
}
